"use client";

import React, { useEffect, useState } from "react";

import { Input } from "@/components/ui";
import { InputProps } from "@/components/ui/input";

import { formatPhoneNumber } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";

interface PhoneNumberInputProps extends InputProps {
  value?: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  className?: string;
}

/**
 * Narrows what the user typed or pasted down to the ten digits of a US number.
 *
 * A leading `1` country code is dropped, so pasting `+1 (555) 123-4567` — or
 * typing it one key at a time — still yields `5551234567`. Anything longer is
 * truncated from the front, which makes an eleventh keystroke on an already
 * full number a no-op instead of rotating the first digit out.
 */
function normalizeEnteredDigits(rawValue: string): string {
  const digits = rawValue.replace(/\D/g, "");
  const withoutCountryCode =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;

  return withoutCountryCode.slice(0, 10);
}

const PhoneNumberInput = React.forwardRef<
  HTMLInputElement,
  PhoneNumberInputProps
>(({ value, onChange, className, ...inputProps }, ref) => {
  const [internalValue, setInternalValue] = useState<string>(
    value ? formatPhoneNumber(value, false) : "",
  );

  useEffect(() => {
    setInternalValue(value ? formatPhoneNumber(value, false) : "");
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const strippedValue = normalizeEnteredDigits(e.target.value);
    setInternalValue(strippedValue);
    if (onChange) {
      // Spreading a DOM node only copies own enumerable properties, so the
      // prototype accessors (`value`, `name`, `id`) are lost. Re-attach the ones
      // form handlers rely on instead of mutating the real input element.
      onChange({
        ...e,
        target: {
          ...e.target,
          name: e.target.name,
          id: e.target.id,
          value: strippedValue,
        },
      });
    }
  };

  return (
    <Input
      ref={ref}
      type="tel"
      id="phoneNumber"
      name="phoneNumber"
      autoComplete="tel-national"
      placeholder="(555) 123-4567"
      className={cx("mx-auto", className)}
      value={
        value
          ? formatPhoneNumber(value, false)
          : formatPhoneNumber(internalValue, false)
      }
      onChange={handleChange}
      {...inputProps}
    />
  );
});

PhoneNumberInput.displayName = "PhoneNumberInput";

export default PhoneNumberInput;
